import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./Sidebar.css";
import {
  MessageSquarePlus,
  Search,
  FileText,
  Database,
  Download,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Wrench,
  HelpCircle
} from "lucide-react";

const Sidebar = ({
  onNavigate,
  currentView,
  onSettingsClick,
  collapsed,
  onToggleCollapse,
  onSelectRecentChat,
  currentChatMode,
  recentChats = [],
  selectedRecentChat = null,
  onDeleteChat,
  displayName = ""
}) => {

  const [openMenuChatId, setOpenMenuChatId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [confirmDeleteChat, setConfirmDeleteChat] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Close dropdown when clicking outside it
  useEffect(() => {
    if (!openMenuChatId) return;
    const close = (e) => {
      if (!e.target.closest('.chat-menu-dropdown') && !e.target.closest('.chat-menu-btn')) {
        setOpenMenuChatId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenuChatId]);

  // Placeholder for recentSearches (no endpoint yet)
  const recentSearches = [
    { id: 1, title: "Health care policy" },
    { id: 2, title: "Safety protocols 2024" }
  ];

  // Placeholder for recentExtractions (prevents crash)
  const recentExtractions = [
    { id: 1, title: "Employee Records" },
    { id: 2, title: "Financial Reports" }
  ];

  const [expandedSections, setExpandedSections] = useState({
    recentChats: true,
    recentSearches: true,
    recentExtractions: true
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isTEAEnvironment = () => {
    return (
      currentChatMode === "TEA" ||
      currentView === "documentSearch" ||
      currentView === "metadataExtractor" ||
      currentView === "exportData"
    );
  };

  const shouldShowQuickActions = () => {
    return currentChatMode === "TEA";
  };

  const safeRecentChats = Array.isArray(recentChats) ? recentChats : [];

  const groupChatsByDate = (chats) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOf7DaysAgo = new Date(startOfToday);
    startOf7DaysAgo.setDate(startOf7DaysAgo.getDate() - 7);
    const startOf30DaysAgo = new Date(startOfToday);
    startOf30DaysAgo.setDate(startOf30DaysAgo.getDate() - 30);

    // Parse a timestamp that may be unix seconds (e.g. 1772025218) or ms or ISO string
    const parseTs = (ts) => {
      if (ts == null) return null;
      const n = Number(ts);
      if (!isNaN(n) && n > 0) {
        // unix seconds if < 10 billion, otherwise already ms
        return new Date(n < 1e10 ? n * 1000 : n);
      }
      const d = new Date(ts);
      return isNaN(d.getTime()) ? null : d;
    };

    const groups = { Today: [], Yesterday: [], 'Last 7 Days': [], 'Last 30 Days': [], Older: [] };
    chats.forEach((chat) => {
      const d = parseTs(chat.timestamp);
      // No timestamp = newly created chat, treat as Today
      if (!d) { groups.Today.push(chat); return; }
      const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (startOfDay >= startOfToday) groups.Today.push(chat);
      else if (startOfDay >= startOfYesterday) groups.Yesterday.push(chat);
      else if (startOfDay >= startOf7DaysAgo) groups['Last 7 Days'].push(chat);
      else if (startOfDay >= startOf30DaysAgo) groups['Last 30 Days'].push(chat);
      else groups.Older.push(chat);
    });
    return groups;
  };

  const handleRecentChatClick = (chat) => {
    if (onSelectRecentChat) {
      onSelectRecentChat(chat);
    }
    onNavigate("chat");
  };

  const handleRecentSearchClick = (searchItem) => {
    const searchChat = {
      id: `search-${searchItem.id}`,
      title: searchItem.title,
      content: `**Search Query:** ${searchItem.title}\n\nI found several documents related to "${searchItem.title}". Would you like me to show the search results?`,
      suggestions: [
        `Show me documents for "${searchItem.title}"`,
        `What are the key findings about ${searchItem.title}?`,
        `Summarize the main points about ${searchItem.title}`
      ]
    };

    if (onSelectRecentChat) {
      onSelectRecentChat(searchChat);
    }
    onNavigate("chat");
  };

  const handleRecentExtractionClick = (extractionItem) => {
    const extractionChat = {
      id: `extraction-${extractionItem.id}`,
      title: extractionItem.title,
      content: `**Metadata Extraction:** ${extractionItem.title}\n\nI've extracted metadata from these files. Would you like to view the results?`,
      suggestions: [
        `Show me the metadata for ${extractionItem.title}`,
        `Export the metadata for ${extractionItem.title}`,
        `What fields were extracted from ${extractionItem.title}?`
      ]
    };

    if (onSelectRecentChat) {
      onSelectRecentChat(extractionChat);
    }
    onNavigate("chat");
  };

  const handleNewChatClick = () => {
    if (onSelectRecentChat) {
      onSelectRecentChat(null);
    }
    onNavigate("chat");
  };

  // Filter recent chats based on search query
  const filteredChats = safeRecentChats.filter((chat) => {
    if (!searchQuery.trim()) return true;
    return chat.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div
      className={`sidebar ${collapsed ? "collapsed" : ""} ${
        isTEAEnvironment() ? "tea-environment" : ""
      }`}
    >
      {/* HEADER */}
      <div className="sidebar-header">
        <div className="logo-container">
          <img src="/logonlng.png" alt="logo" className="logo" />
          {!collapsed && <h1 className="main-title">Nimi</h1>}
        </div>
        <button className="collapse-btn" onClick={onToggleCollapse}>
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* CONTENT */}
      <div className="sidebar-content">
        {!collapsed ? (
          <>
            {/* NEW CHAT & SEARCH */}
            <div 
              className="nav-buttons-section"
              style={{
                opacity: isTEAEnvironment() ? 0.5 : 1,
                pointerEvents: isTEAEnvironment() ? 'none' : 'auto'
              }}
            >
              <button
                className={`nav-btn ${
                  currentView === "chat" ? "active" : ""
                }`}
                onClick={handleNewChatClick}
                disabled={isTEAEnvironment()}
                style={{
                  cursor: isTEAEnvironment() ? "not-allowed" : "pointer"
                }}
              >
                <MessageSquarePlus size={18} />
                <span>New chat</span>
              </button>

              <div
                className="search-input-field"
              >
                <Search size={18} className="search-input-icon" />
                <input
                  type="text"
                  placeholder="Search chats..."
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isTEAEnvironment()}
                  style={{height: '28px', background: 'transparent', border: 'none', textAlign: 'left', paddingLeft: '4px'}}
                />
                {searchQuery && !isTEAEnvironment() && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* TEA Indicator */}
            {isTEAEnvironment() && (
              <div className="tea-indicator-sections">
                <div className="tea-indicator">
                  <Sparkles size={16} />
                  <span>Quick Actions</span>
                </div>
              </div>
            )}

            {/* QUICK ACTIONS */}
            {shouldShowQuickActions() && (
              <div className="section">
                <div className="quick-actions-verticals">
                  <div
                    className={`quick-action-item ${
                      currentView === "documentSearch" ? "active" : ""
                    }`}
                    onClick={() => onNavigate("documentSearch")}
                  >
                    <FileText size={16} />
                    <span>Document Search</span>
                  </div>

                  <div
                    className={`quick-action-item ${
                      currentView === "metadataExtractor" ? "active" : ""
                    }`}
                    onClick={() => onNavigate("metadataExtractor")}
                  >
                    <Database size={16} />
                    <span>Metadata Extractor</span>
                  </div>

                </div>
              </div>
            )}

            {/* RECENT CHATS */}
            {!isTEAEnvironment() && (
              <div className="section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("recentChats")}
                >
                  <span className="section-title">Recent chats</span>
                  {expandedSections.recentChats ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>

                {expandedSections.recentChats && (
                  <>
                    <div className="plain-list">
                      {(() => {
                        const groups = groupChatsByDate(filteredChats);
                        const labels = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'Older'];
                        return labels.flatMap((label) => {
                          const chats = groups[label];
                          if (!chats || chats.length === 0) return [];
                          return [
                            <div key={`grp-${label}`} className="chat-date-group-header">{label}</div>,
                            ...chats.map((chat) => (
                              <div
                                key={chat.id}
                                className={`plain-item recent-chat-item${selectedRecentChat?.id === chat.id ? ' active-chat' : ''}`}
                                onClick={() => handleRecentChatClick(chat)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}
                              >
                                <div className="plain-item-title" style={{ flex: 1, minWidth: 0 }}>
                                  {chat.title}
                                </div>
                                <div className="chat-menu-wrapper" onClick={e => e.stopPropagation()}>
                                  <button
                                    className="chat-menu-btn"
                                    title="Chat options"
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (openMenuChatId === chat.id) {
                                        setOpenMenuChatId(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                        setOpenMenuChatId(chat.id);
                                      }
                                    }}
                                  >⋯</button>
                                  {openMenuChatId === chat.id && ReactDOM.createPortal(
                                    <div
                                      className="chat-menu-dropdown"
                                      style={{ top: menuPosition.top, right: menuPosition.right }}
                                    >
                                      <button
                                        className="chat-menu-item delete"
                                        onClick={() => {
                                          setOpenMenuChatId(null);
                                          setConfirmDeleteChat(chat);
                                        }}
                                      >
                                        🗑 Delete Chat
                                      </button>
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              </div>
                            ))
                          ];
                        });
                      })()}
                      {filteredChats.length === 0 && (
                        <div className="empty-state">
                          {searchQuery ? "No chats match your search" : "No recent chats"}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* RECENT SEARCHES - Only for TEA AI 
            {isTEAEnvironment() && (
              <div className="section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("recentSearches")}
                >
                  <span className="section-title">
                    Recent document search
                  </span>
                  {expandedSections.recentSearches ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>

                {expandedSections.recentSearches && (
                  <div className="plain-list">
                    {recentSearches.map((item) => (
                      <div
                        key={item.id}
                        className="plain-item"
                        onClick={() =>
                          handleRecentSearchClick(item)
                        }
                      >
                        {item.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}*/}

            {/* RECENT EXTRACTIONS - Only for TEA AI */}
            {isTEAEnvironment() && (
              <div className="section">

              </div>
            )}
          </>
        ) : (
          /* COLLAPSED MODE */
          <div className="collapsed-content">
            <div className="collapsed-menu-top">
              {!isTEAEnvironment() && (
                <>
                  <button
                    className="collapsed-btn"
                    onClick={handleNewChatClick}
                    title="New Chat"
                  >
                    <MessageSquarePlus size={20} />
                  </button>

                  <button
                    className="collapsed-btn"
                    onClick={() => onNavigate("documentSearch")}
                    title="Document Search"
                  >
                    <Search size={20} />
                  </button>
                </>
              )}

              {shouldShowQuickActions() && (
                <>
                  <button
                    className="collapsed-btn"
                    onClick={() => onNavigate("documentSearch")}
                  >
                    <FileText size={20} />
                  </button>
                  <button
                    className="collapsed-btn"
                    onClick={() =>
                      onNavigate("metadataExtractor")
                    }
                  >
                    <Database size={20} />
                  </button>
                </>
              )}
            </div>

            <div className="collapsed-menu-bottom">
              <button
                className="collapsed-btn"
                onClick={() => window.open('https://nlng.sharepoint.com/:b:/s/M365-DigitalPMOCollaboration/IQA19h2XZfGuR5TjF7OqkCReAXJ2f0wuqwYX_CnaTXRh3cA?e=lQnofS', '_blank')}
                title="Open User Guide"
              >
                <HelpCircle size={20} />
              </button>
              <button
                className="collapsed-btn"
                onClick={onSettingsClick}
              >
                <Settings size={20} />
              </button>
              <div className="collapsed-user">
                <User size={20} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      {!collapsed && (
        <div className="sidebar-footer">
          <button
            className="settings-btn"
            onClick={() => window.open('https://nlng.sharepoint.com/:b:/s/M365-DigitalPMOCollaboration/IQA19h2XZfGuR5TjF7OqkCReAXJ2f0wuqwYX_CnaTXRh3cA?e=lQnofS', '_blank')}
            title="Open User Guide"
          >
            <HelpCircle size={18} />
            <span>User Guide</span>
          </button>

          <button
            className="settings-btn"
            onClick={onSettingsClick}
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>

          <div className="user-info">
            <User size={20} className="user-avatar" />
            <div className="user-details">
              <div className="user-name">
                {/* Extract 'Firstname Lastname' from displayName like 'Ore, Juwon (CSD/219C) NLNGPHC' */}
                {(() => {
                  if (!displayName) return "User";
                  // Example: 'Ore, Juwon (CSD/219C) NLNGPHC'
                  // Remove everything after '(' if present
                  const namePart = displayName.split('(')[0].trim();
                  // If comma, format as 'Firstname Lastname'
                  if (namePart.includes(',')) {
                    const [last, first] = namePart.split(',').map(s => s.trim());
                    return `${first} ${last}`;
                  }
                  return namePart;
                })()}
              </div>
              <div className="user-role">
                {/* Extract department code from displayName like 'Ore, Juwon (CSD/219C) NLNGPHC' */}
                {(() => {
                  if (!displayName) return "";
                  const match = displayName.match(/\(([^)]+)\)/);
                  return match ? match[1] : "";
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteChat && ReactDOM.createPortal(
        <div className="delete-confirm-overlay" onClick={() => setConfirmDeleteChat(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-icon">🗑️</div>
            <h2 className="delete-confirm-title">Delete Chat?</h2>
            <p className="delete-confirm-body">
              Are you sure you want to delete <strong>"{confirmDeleteChat.title}"</strong>?<br />
              This action <strong>cannot be undone</strong>.
            </p>
            <div className="delete-confirm-actions">
              <button
                className="delete-confirm-btn cancel"
                onClick={() => setConfirmDeleteChat(null)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-btn confirm"
                onClick={() => {
                  const chatId = confirmDeleteChat.id;
                  setConfirmDeleteChat(null);
                  if (typeof onDeleteChat === 'function') onDeleteChat(chatId);
                  setFlashMessage('Chat deleted successfully');
                  setTimeout(() => setFlashMessage(null), 3000);
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {flashMessage && ReactDOM.createPortal(
        <div className="delete-flash-toast">
          <span className="delete-flash-icon">✓</span>
          {flashMessage}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Sidebar;